import { fetchRaSupplierRoster } from '../../services/supplierRosterCache'
import { hideSimulatedSuppliers, setSimulatedSupplierCompany } from './simulatedSuppliers'
import { supabase } from '../../services/supabase'
import type { Supplier, SupplierMitigationTask, SupplierNews } from '../../types'
import {
  bandCountsUnique,
  collapseSuppliersByName,
  collapseSuppliersByParentName,
  collapsedBand,
  formatBrandsFull,
  type CollapsedSupplierRisk,
} from './supplierRollup'
import { supplierSearchTokens } from './supplierRiskRatingsTable'
import { spendFactsSection } from './spendAnalystAnswers'

const ALERT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

export type FactsPackMode = 'next_best_actions' | 'questions_to_ask'

/**
 * Deterministic facts pack for narrative Risk Analyst asks.
 * LLM may phrase answers but must not invent rows beyond this pack.
 */
export async function buildRiskAnalystFactsPack(options: {
  mode: FactsPackMode
  supplierName?: string | null
  companyId?: string | null
}): Promise<string> {
  const { mode, supplierName } = options
  setSimulatedSupplierCompany(options.companyId)
  const sections: string[] = [
    `Facts pack mode: ${mode}`,
    'Answer ONLY using the facts below. Do not invent suppliers, scores, brands, alerts, mitigations, or spend figures that are not listed.',
    'Synthesize a markdown table of recommended actions or questions FROM these facts (that is required). Do not refuse if any section below has real rows.',
  ]

  const suppliers = hideSimulatedSuppliers(await fetchRaSupplierRoster())
  const directScoped = suppliers.filter((s) => s.procurement_type !== 'indirect')
  const indirectScoped = suppliers.filter((s) => s.procurement_type === 'indirect')

  const directCollapsed = collapseSuppliersByName(directScoped)
  const indirectCollapsed = collapseSuppliersByParentName(indirectScoped)

  pushPortfolioHighRiskSection(sections, 'Direct', directCollapsed)
  pushPortfolioHighRiskSection(sections, 'Indirect', indirectCollapsed)

  const [{ data: tasksRaw, error: tErr }, { data: newsRaw, error: nErr }] = await Promise.all([
    supabase
      .from('supplier_mitigation_tasks')
      .select('*')
      .order('alert_date', { ascending: false })
      .limit(200),
    supabase.from('supplier_news').select('*').order('published_at', { ascending: false }).limit(200),
  ])
  if (tErr) throw new Error(tErr.message || 'Failed to load mitigations for facts pack.')
  if (nErr) throw new Error(nErr.message || 'Failed to load alerts for facts pack.')

  const idToName = new Map(suppliers.map((s) => [s.id, s.name]))
  const tasks = ((tasksRaw ?? []) as SupplierMitigationTask[]).filter(
    (t) => t.status !== 'closed' && t.status !== 'completed'
  )
  sections.push(
    '## Open mitigations (sample)',
    ...tasks.slice(0, 15).map((t) => {
      const name = t.supplier_id ? idToName.get(t.supplier_id) || 'Unknown' : 'Unknown'
      return `- ${name} | ${t.status} | ${t.task || '—'}${t.brand ? ` | brand ${t.brand}` : ''}`
    }),
    tasks.length === 0 ? '- (none)' : ''
  )

  const cutoff = Date.now() - ALERT_WINDOW_MS
  const recent = ((newsRaw ?? []) as SupplierNews[])
    .filter((n) => {
      const d = n.published_at || n.created_at
      if (!d) return true
      return new Date(d).getTime() >= cutoff
    })
    .slice(0, 15)
  sections.push(
    '## Recent alerts (7 days, sample)',
    ...recent.map((n) => {
      const name = n.supplier_id ? idToName.get(n.supplier_id) || 'Unknown' : 'Unknown'
      return `- ${name} | ${n.headline || '—'}`
    }),
    recent.length === 0 ? '- (none)' : ''
  )

  if (supplierName?.trim()) {
    const focused = filterSuppliersByName(suppliers, supplierName.trim())
    sections.push(`## Focus supplier query: ${supplierName.trim()}`)
    if (focused.length === 0) {
      sections.push('- No matching supplier rows.')
    } else {
      const names = [...new Set(focused.map((s) => s.name))]
      sections.push(`- Matching legal names: ${names.join('; ')}`)
      const brands = [
        ...new Set(focused.map((s) => (s.brand || '').trim()).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b))
      sections.push(`- Brands: ${brands.join(', ') || '—'}`)
      const ids = new Set(focused.map((s) => s.id))
      const focusTasks = tasks.filter((t) => t.supplier_id && ids.has(t.supplier_id)).slice(0, 10)
      sections.push(
        '### Open mitigations for focus supplier',
        ...focusTasks.map((t) => `- ${t.status} | ${t.task || '—'}`),
        focusTasks.length === 0 ? '- (none)' : ''
      )
      const focusNews = ((newsRaw ?? []) as SupplierNews[])
        .filter((n) => n.supplier_id && ids.has(n.supplier_id))
        .slice(0, 10)
      sections.push(
        '### Recent/related news for focus supplier',
        ...focusNews.map((n) => `- ${n.headline || '—'}`),
        focusNews.length === 0 ? '- (none)' : ''
      )
    }
  }

  pushPortfolioBandSection(sections, 'Direct', directCollapsed)
  pushPortfolioBandSection(sections, 'Indirect', indirectCollapsed)

  sections.push(...(await spendFactsSection(options.companyId ?? null, supplierName)))

  return sections.filter((l) => l !== '').join('\n')
}

function pushPortfolioHighRiskSection(
  sections: string[],
  label: 'Direct' | 'Indirect',
  collapsed: CollapsedSupplierRisk[]
) {
  const highRisk = collapsed.filter((s) => collapsedBand(s) === 'high')
  sections.push(
    `## Top high-risk ${label} suppliers (unique ${label === 'Indirect' ? 'parents' : 'names'}, brands combined)`,
    ...highRisk.slice(0, 12).map(
      (s) =>
        `- ${s.name} | score ${s.overall_score != null ? s.overall_score.toFixed(1) : '—'} | ${s.location || '—'} | brands: ${formatBrandsFull(s.brands) || '—'}`
    ),
    highRisk.length === 0 ? '- (none)' : '',
    `High-risk brand impacts: ${highRisk.reduce((n, s) => n + Math.max(s.brands.length, 1), 0)}`
  )
}

function pushPortfolioBandSection(
  sections: string[],
  label: 'Direct' | 'Indirect',
  collapsed: CollapsedSupplierRisk[]
) {
  const bands = bandCountsUnique(collapsed)
  const brandImpacts = collapsed.reduce((n, s) => n + Math.max(s.brands.length, 1), 0)
  sections.push(
    `## ${label} portfolio band counts (unique ${label === 'Indirect' ? 'parents' : 'suppliers'}, highest score)`,
    `- total ${bands.total}; high ${bands.high}; medium ${bands.medium}; low ${bands.low}; unscored ${bands.unscored}`,
    `- brand impacts (all bands): ${brandImpacts}`
  )
}

function filterSuppliersByName(suppliers: Supplier[], nameQuery: string): Supplier[] {
  const tokens = supplierSearchTokens(nameQuery)
  const q = nameQuery.toLowerCase()
  let rows = suppliers.filter((s) => (s.name || '').toLowerCase().includes(q))
  if (rows.length === 0 && tokens[0]) {
    const t0 = tokens[0].toLowerCase()
    rows = suppliers.filter((s) => (s.name || '').toLowerCase().includes(t0))
  }
  if (tokens.length > 1) {
    rows = rows.filter((s) => {
      const n = (s.name || '').toLowerCase()
      return tokens.every((t) => n.includes(t.toLowerCase()))
    })
  }
  return rows
}
